import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import {
  ProgramaAnexoDetalle,
  ProgramaCampaniaDetalle,
  ProgramaItemStore,
  ProgramaTarifaDetalle
} from '../cotizador-contratante/programa-seleccion.store';

// Keys LS beneficiario
export const KEY_DATOS_RQ003_BENEFICIARIO = 'datosRQ003_Beneficiario';
export const KEY_PROGRAMAS_BENEFICIARIO = 'programas_beneficiario';
const KEY_PROGRAMAS_CONTRATANTE = 'programas_contratante';
const KEY_DATOS_CONTRATANTE = 'datosRQ002_Contratante';

/** Datos del tab beneficiario en localStorage */
export interface DatosBeneficiarioLocal {
  localId?: string;
  parentesco?: string;
  contratanteTipoDocumento: string;
  contratanteDocumento: string;
  nombre?: string;
  nombreSegundo?: string;
  apellido?: string;
  apellidoSegundo?: string;
  genero?: string;
  fechaNacimiento?: string;
  edad?: string;
  lineaNegocios?: string;
  programa?: string;
  tipoVia?: string;
  numero?: string;
  placaUno?: string;
  placaDos?: string;
  ciudad?: string;
  subcuenta?: string;
  tarifa?: string;
  tarifaAnexo?: string;
  campania?: string;
  usarProgramasContratante?: boolean;
  /** Id en BD luego de Continuar */
  idBeneficiario?: number;
}

/** Envelope de programas del beneficiario (misma forma que programas_contratante) */
export interface ProgramasBeneficiarioEnvelope {
  localId?: string;
  idCotizacion: number;
  tipoDocumento: string;
  numeroDocumento: string;
  parentesco?: string;
  savedAt: string;
  programas: ProgramaItemStore[];
  idBeneficiario?: number;
  /** Programas del contratante que el usuario quitó a propósito */
  programasExcluidos?: string[];
  /** Edad/sexo/parentesco para recalcular tarifas tipo I */
  datosRecalculo?: {
    edad: string;
    sexo: string;
    parentesco: string;
    cuenta?: string;
    tipoPlan?: string;
  };
}

/** Clave para ubicar un beneficiario en LS */
export interface BeneficiarioLocalMatchKey {
  localId?: string;
  tipoDocumento?: string;
  numeroDocumento?: string;
  parentesco?: string;
}

export type {
  ProgramaItemStore,
  ProgramaTarifaDetalle,
  ProgramaAnexoDetalle,
  ProgramaCampaniaDetalle
};

@Injectable({ providedIn: 'root' })
export class BeneficiarioLocalStorageService {

  /** Avisa cuántos beneficiarios se sincronizaron al heredar del contratante */
  readonly programasHeredadosSincronizados$ = new Subject<number>();

  cargarDatos(): DatosBeneficiarioLocal[] {
    return this.leerArreglo<DatosBeneficiarioLocal>(KEY_DATOS_RQ003_BENEFICIARIO);
  }

  /** Agrega un beneficiario nuevo (siempre al final) */
  agregarDatos(item: DatosBeneficiarioLocal): void {
    const lista = this.cargarDatos();
    lista.push(item);
    this.escribir(KEY_DATOS_RQ003_BENEFICIARIO, lista);
  }

  cargarProgramas(): ProgramasBeneficiarioEnvelope[] {
    return this.leerArreglo<ProgramasBeneficiarioEnvelope>(KEY_PROGRAMAS_BENEFICIARIO);
  }

  /** Agrega el envelope de programas (siempre al final) */
  agregarProgramas(envelope: ProgramasBeneficiarioEnvelope): void {
    const lista = this.cargarProgramas();
    lista.push({
      ...envelope,
      savedAt: envelope.savedAt || new Date().toISOString(),
      programas: Array.isArray(envelope.programas) ? envelope.programas : []
    });
    this.escribir(KEY_PROGRAMAS_BENEFICIARIO, lista);
  }

  /** Guarda datos y programas de un alta */
  guardarBeneficiarioNuevo(
    datos: DatosBeneficiarioLocal,
    programas: ProgramasBeneficiarioEnvelope
  ): void {
    const savedAt = new Date().toISOString();
    const localId = (datos.localId || programas.localId || this.generarLocalId()).toString().trim();
    let programasItems = Array.isArray(programas.programas) ? programas.programas : [];
    if (datos.usarProgramasContratante === true) {
      programasItems = this.aplicarMarcasPersonalizacion(programasItems, [], null);
    } else {
      programasItems = programasItems.map(p => ({
        ...p,
        personalizado: false,
        codigoOrigenContratante: null
      }));
    }
    this.agregarDatos({ ...datos, localId });
    this.agregarProgramas({
      ...programas,
      localId,
      savedAt,
      programas: programasItems,
      datosRecalculo: this.construirDatosRecalculo(datos),
      programasExcluidos: datos.usarProgramasContratante === true
        ? this.calcularProgramasExcluidos(programasItems)
        : []
    });
  }

  /** Actualiza por localId o tipo+documento+parentesco. Devuelve false si no existe */
  actualizarBeneficiario(
    match: BeneficiarioLocalMatchKey,
    datos: DatosBeneficiarioLocal,
    programas: ProgramasBeneficiarioEnvelope
  ): boolean {
    const listaDatos = this.cargarDatos();
    const listaProgramas = this.cargarProgramas();
    const idx = this.findIndexDatos(listaDatos, match);
    if (idx < 0) {
      return false;
    }

    const localId =
      (match.localId || listaDatos[idx]?.localId || datos.localId || this.generarLocalId()).toString().trim();
    const savedAt = new Date().toISOString();

    const idxProgPrev = this.findIndexProgramas(listaProgramas, { ...match, localId }, idx);
    const prevProgramas = idxProgPrev >= 0
      ? (Array.isArray(listaProgramas[idxProgPrev]?.programas) ? listaProgramas[idxProgPrev].programas : [])
      : [];
    const prevIdBeneficiario = Number(
      (listaDatos[idx] as any)?.idBeneficiario
      || (idxProgPrev >= 0 ? listaProgramas[idxProgPrev]?.idBeneficiario : 0)
      || 0
    );

    let programasItems = Array.isArray(programas.programas) ? programas.programas : [];
    if (datos.usarProgramasContratante === true) {
      programasItems = this.aplicarMarcasPersonalizacion(programasItems, prevProgramas, null);
    } else {
      programasItems = programasItems.map(p => ({
        ...p,
        personalizado: false,
        codigoOrigenContratante: null
      }));
    }
    programasItems = this.conservarIdsPersistenciaProgramas(prevProgramas, programasItems);

    listaDatos[idx] = {
      ...datos,
      localId,
      idBeneficiario: Number((datos as any)?.idBeneficiario || prevIdBeneficiario || 0) || undefined
    };
    this.escribir(KEY_DATOS_RQ003_BENEFICIARIO, listaDatos);

    const idxProg = this.findIndexProgramas(listaProgramas, { ...match, localId }, idx);
    const envelope: ProgramasBeneficiarioEnvelope = {
      ...programas,
      localId,
      savedAt,
      idBeneficiario: Number(
        (datos as any)?.idBeneficiario
        || prevIdBeneficiario
        || (idxProg >= 0 ? listaProgramas[idxProg]?.idBeneficiario : 0)
        || 0
      ) || undefined,
      programas: programasItems,
      datosRecalculo: this.construirDatosRecalculo(datos),
      programasExcluidos: datos.usarProgramasContratante === true
        ? this.calcularProgramasExcluidos(programasItems)
        : []
    };
    if (idxProg >= 0) {
      listaProgramas[idxProg] = envelope;
    } else if (idx < listaProgramas.length) {
      listaProgramas[idx] = envelope;
    } else {
      listaProgramas.push(envelope);
    }
    this.escribir(KEY_PROGRAMAS_BENEFICIARIO, listaProgramas);
    return true;
  }

  generarLocalId(): string {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch { /* no-op */ }
    return `ben-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  limpiarTodo(): void {
    try {
      localStorage.removeItem(KEY_DATOS_RQ003_BENEFICIARIO);
      localStorage.removeItem(KEY_PROGRAMAS_BENEFICIARIO);
    } catch { /* no-op */ }
  }

  /** Quita un programa. Si era el último, borra todo el beneficiario */
  eliminarPrograma(
    match: BeneficiarioLocalMatchKey,
    criterio: { programaCodigo?: string; programaNombre?: string; lineaNegocio?: string }
  ): 'programa' | 'beneficiario' | false {
    const listaDatos = this.cargarDatos();
    const listaProgramas = this.cargarProgramas();
    const idxDatos = this.findIndexDatos(listaDatos, match);
    const idxProg = this.findIndexProgramas(listaProgramas, match, idxDatos);
    if (idxProg < 0) {
      return false;
    }

    const envelope = listaProgramas[idxProg];
    const programas = Array.isArray(envelope?.programas) ? [...envelope.programas] : [];
    const restantes = programas.filter(p => !this.programaCoincide(p, criterio));

    if (restantes.length === programas.length) {
      return false;
    }

    const removidos = programas.filter(p => this.programaCoincide(p, criterio));

    if (restantes.length === 0) {
      if (idxProg >= 0) listaProgramas.splice(idxProg, 1);
      if (idxDatos >= 0) listaDatos.splice(idxDatos, 1);
      this.escribir(KEY_PROGRAMAS_BENEFICIARIO, listaProgramas);
      this.escribir(KEY_DATOS_RQ003_BENEFICIARIO, listaDatos);
      return 'beneficiario';
    }

    listaProgramas[idxProg] = {
      ...envelope,
      savedAt: new Date().toISOString(),
      programas: restantes,
      programasExcluidos: this.unirProgramasExcluidos(
        envelope.programasExcluidos,
        removidos,
        criterio
      )
    };
    this.escribir(KEY_PROGRAMAS_BENEFICIARIO, listaProgramas);

    if (idxDatos >= 0) {
      const datos = listaDatos[idxDatos];
      listaDatos[idxDatos] = {
        ...datos,
        programa: restantes
          .map(p => (p?.programa || '').toString().trim())
          .filter(Boolean)
          .join(', ') || undefined,
        lineaNegocios: this.lineasNegocioDesdeProgramas(restantes) || undefined
      };
      this.escribir(KEY_DATOS_RQ003_BENEFICIARIO, listaDatos);
    }

    return 'programa';
  }

  /** Borra el beneficiario de ambas keys */
  eliminarBeneficiario(match: BeneficiarioLocalMatchKey): boolean {
    const listaDatos = this.cargarDatos();
    const listaProgramas = this.cargarProgramas();
    const idxDatos = this.findIndexDatos(listaDatos, match);
    const idxProg = this.findIndexProgramas(listaProgramas, match, idxDatos);
    if (idxDatos < 0 && idxProg < 0) {
      return false;
    }
    if (idxDatos >= 0) listaDatos.splice(idxDatos, 1);
    if (idxProg >= 0) listaProgramas.splice(idxProg, 1);
    this.escribir(KEY_DATOS_RQ003_BENEFICIARIO, listaDatos);
    this.escribir(KEY_PROGRAMAS_BENEFICIARIO, listaProgramas);
    return true;
  }

  /** Guarda en LS los ids de BD después de Continuar */
  persistirIdsBeneficiario(
    match: BeneficiarioLocalMatchKey,
    idBeneficiario: number,
    idCotizacion: number,
    idsPorPrograma: Record<string, {
      idBenPrograma?: number;
      idSubcuenta?: number;
      idCotizacionTarifa?: number;
      idCotizacionTarifaAnexo?: number;
      idCotizacionCampania?: number;
    }>
  ): boolean {
    const listaDatos = this.cargarDatos();
    const listaProgramas = this.cargarProgramas();
    const idxDatos = this.findIndexDatos(listaDatos, match);
    if (idxDatos < 0) return false;

    const localId = (
      match.localId || listaDatos[idxDatos]?.localId || this.generarLocalId()
    ).toString().trim();

    listaDatos[idxDatos] = {
      ...listaDatos[idxDatos],
      localId,
      idBeneficiario: idBeneficiario > 0 ? idBeneficiario : undefined
    };

    const idxProg = this.findIndexProgramas(listaProgramas, { ...match, localId }, idxDatos);
    const envelopeBase = idxProg >= 0
      ? listaProgramas[idxProg]
      : {
          localId,
          idCotizacion,
          tipoDocumento: (listaDatos[idxDatos].contratanteTipoDocumento || '').toString().trim(),
          numeroDocumento: (listaDatos[idxDatos].contratanteDocumento || '').toString().trim(),
          parentesco: (listaDatos[idxDatos].parentesco || '').toString().trim() || undefined,
          savedAt: new Date().toISOString(),
          programas: [] as ProgramaItemStore[]
        };

    const programasActualizados = (Array.isArray(envelopeBase.programas) ? envelopeBase.programas : []).map((p) => {
      const keys = this.clavesProgramaPersistencia(p);
      let ids: {
        idBenPrograma?: number;
        idSubcuenta?: number;
        idCotizacionTarifa?: number;
        idCotizacionTarifaAnexo?: number;
        idCotizacionCampania?: number;
      } = {};
      for (const key of keys) {
        if (idsPorPrograma[key]) {
          ids = idsPorPrograma[key];
          break;
        }
      }
      const pickId = (nuevo?: number, ...prev: any[]) => {
        const n = Number(nuevo || 0);
        if (n > 0) return n;
        for (const v of prev) {
          const pv = Number(v || 0);
          if (pv > 0) return pv;
        }
        return null;
      };
      return {
        ...p,
        idBeneficiario: idBeneficiario > 0 ? idBeneficiario : (p as any)?.idBeneficiario,
        idBenPrograma: pickId(ids.idBenPrograma, (p as any)?.idBenPrograma, (p as any)?.__idBenPrograma),
        idSubcuenta: pickId(ids.idSubcuenta, (p as any)?.idSubcuenta, (p as any)?.__idSubcuenta),
        idCotizacionTarifa: pickId(ids.idCotizacionTarifa, (p as any)?.idCotizacionTarifa, (p as any)?.__idCotizacionTarifa),
        idCotizacionTarifaAnexo: pickId(ids.idCotizacionTarifaAnexo, (p as any)?.idCotizacionTarifaAnexo, (p as any)?.__idCotizacionTarifaAnexo),
        idCotizacionCampania: pickId(
          ids.idCotizacionCampania,
          (p as any)?.idCotizacionCampania,
          (p as any)?.__idCotizacionCampania,
          (p as any)?.idBenCampania
        ),
        guardadoEnBd: true
      } as ProgramaItemStore;
    });

    const envelope: ProgramasBeneficiarioEnvelope = {
      ...envelopeBase,
      localId,
      idCotizacion: idCotizacion > 0 ? idCotizacion : Number(envelopeBase.idCotizacion || 0) || 0,
      idBeneficiario: idBeneficiario > 0 ? idBeneficiario : undefined,
      savedAt: new Date().toISOString(),
      programas: programasActualizados
    };

    if (idxProg >= 0) {
      listaProgramas[idxProg] = envelope;
    } else {
      listaProgramas.push(envelope);
    }

    this.escribir(KEY_DATOS_RQ003_BENEFICIARIO, listaDatos);
    this.escribir(KEY_PROGRAMAS_BENEFICIARIO, listaProgramas);
    return true;
  }

  /** Quita ids de BD tras un delete en cascada */
  limpiarIdsPersistenciaBeneficiario(match: BeneficiarioLocalMatchKey): void {
    const listaDatos = this.cargarDatos();
    const listaProgramas = this.cargarProgramas();
    const idxDatos = this.findIndexDatos(listaDatos, match);
    if (idxDatos < 0) return;

    const localId = (match.localId || listaDatos[idxDatos]?.localId || '').toString().trim();
    const { idBeneficiario: _omitId, ...datosSinId } = listaDatos[idxDatos] as DatosBeneficiarioLocal & { idBeneficiario?: number };
    listaDatos[idxDatos] = { ...datosSinId, localId: localId || datosSinId.localId };

    const idxProg = this.findIndexProgramas(listaProgramas, { ...match, localId }, idxDatos);
    if (idxProg >= 0) {
      const env = listaProgramas[idxProg];
      listaProgramas[idxProg] = {
        ...env,
        idBeneficiario: undefined,
        programas: this.limpiarIdsPersistenciaProgramas(env.programas || [])
      };
    }

    this.escribir(KEY_DATOS_RQ003_BENEFICIARIO, listaDatos);
    this.escribir(KEY_PROGRAMAS_BENEFICIARIO, listaProgramas);
  }

  /** Quita ids de hijos y deja el idBeneficiario */
  limpiarIdsHijosProgramasPreservandoBeneficiario(idsBeneficiario: Iterable<number>): void {
    const ids = new Set(Array.from(idsBeneficiario).map((n) => Number(n)).filter((n) => n > 0));
    if (!ids.size) return;

    const listaProgramas = this.cargarProgramas();
    let changed = false;
    const next = listaProgramas.map((env) => {
      const idBen = Number((env as any)?.idBeneficiario || 0);
      if (!(idBen > 0) || !ids.has(idBen)) return env;
      changed = true;
      return {
        ...env,
        programas: this.limpiarIdsPersistenciaProgramas(env.programas || [])
      };
    });
    if (changed) {
      this.escribir(KEY_PROGRAMAS_BENEFICIARIO, next);
    }
  }

  /**
   * Copia programas del contratante a beneficiarios con el switch ON.
   * Respeta exclusiones y overrides. Si el contratante queda vacío, los borra.
   */
  sincronizarProgramasDesdeContratante(programasFuente?: ProgramaItemStore[] | null): number {
    try {
      const programasContratante = this.normalizarProgramasFuente(programasFuente);
      const listaDatos = this.cargarDatos();
      if (!listaDatos.length) {
        return 0;
      }

      const docTitular = this.leerDocumentoContratante();
      const listaProgramas = this.cargarProgramas();
      const sinProgramasContratante = programasContratante.length === 0;
      let affected = 0;

      for (let i = listaDatos.length - 1; i >= 0; i--) {
        const datos = listaDatos[i];
        if (!datos || datos.usarProgramasContratante !== true) {
          continue;
        }
        if (this.esBeneficiarioTitularContratante(datos, docTitular)) {
          continue;
        }

        const localId = (datos.localId || this.generarLocalId()).toString().trim();
        const match: BeneficiarioLocalMatchKey = {
          localId,
          tipoDocumento: datos.contratanteTipoDocumento,
          numeroDocumento: datos.contratanteDocumento,
          parentesco: datos.parentesco
        };
        const idxProg = this.findIndexProgramas(listaProgramas, match, -1);
        const envelopeBase =
          idxProg >= 0 ? listaProgramas[idxProg] : null;

        const savedAt = new Date().toISOString();

        if (sinProgramasContratante) {
          // Al cambiar el tipo de plan, conservar al beneficiario.
          // Únicamente se limpian los programas heredados del plan anterior.
          listaDatos[i] = {
            ...datos,
            localId,
            programa: undefined,
            lineaNegocios: undefined,
          };

          const envelopeSinProgramas: ProgramasBeneficiarioEnvelope = {
            ...(envelopeBase || {}),
            localId,
            idCotizacion:
              Number(envelopeBase?.idCotizacion || 0) || 0,
            tipoDocumento: (
              datos.contratanteTipoDocumento ||
              envelopeBase?.tipoDocumento ||
              ''
            ).toString().trim(),
            numeroDocumento: (
              datos.contratanteDocumento ||
              envelopeBase?.numeroDocumento ||
              ''
            ).toString().trim(),
            parentesco: (
              datos.parentesco ||
              envelopeBase?.parentesco ||
              ''
            ).toString().trim() || undefined,
            savedAt,
            idBeneficiario:
              Number(
                datos.idBeneficiario ||
                envelopeBase?.idBeneficiario ||
                0
              ) || undefined,
            programas: [],
            datosRecalculo: this.construirDatosRecalculo(datos),
            programasExcluidos: [],
          };

          if (idxProg >= 0) {
            listaProgramas[idxProg] = envelopeSinProgramas;
          } else {
            listaProgramas.push(envelopeSinProgramas);
          }

          affected++;
          continue;
        }

        const prevProgramas = Array.isArray(envelopeBase?.programas) ? envelopeBase!.programas : [];
        const exclusiones = this.normalizarProgramasExcluidos(envelopeBase?.programasExcluidos);
        const merged = this.mergeProgramasHeredados(prevProgramas, programasContratante, exclusiones);

        listaDatos[i] = {
          ...datos,
          localId,
          usarProgramasContratante: true,
          programa: merged
            .map(p => (p?.programa || '').toString().trim())
            .filter(Boolean)
            .join(', ') || undefined,
          lineaNegocios: this.lineasNegocioDesdeProgramas(merged) || undefined
        };

        const envelope: ProgramasBeneficiarioEnvelope = {
          localId,
          idCotizacion: Number(envelopeBase?.idCotizacion || 0) || 0,
          tipoDocumento: (datos.contratanteTipoDocumento || envelopeBase?.tipoDocumento || '').toString().trim(),
          numeroDocumento: (datos.contratanteDocumento || envelopeBase?.numeroDocumento || '').toString().trim(),
          parentesco: (datos.parentesco || envelopeBase?.parentesco || '').toString().trim() || undefined,
          savedAt,
          idBeneficiario: Number(datos.idBeneficiario || envelopeBase?.idBeneficiario || 0) || undefined,
          programas: merged,
          datosRecalculo: this.construirDatosRecalculo(datos),
          programasExcluidos: exclusiones
        };

        if (idxProg >= 0) {
          listaProgramas[idxProg] = envelope;
        } else {
          listaProgramas.push(envelope);
        }
        affected++;
      }

      if (affected > 0) {
        if (!listaDatos.length) {
          try {
            localStorage.removeItem(KEY_DATOS_RQ003_BENEFICIARIO);
            localStorage.removeItem(KEY_PROGRAMAS_BENEFICIARIO);
          } catch { /* no-op */ }
        } else {
          this.escribir(KEY_DATOS_RQ003_BENEFICIARIO, listaDatos);
          this.escribir(KEY_PROGRAMAS_BENEFICIARIO, listaProgramas);
        }
        try {
          this.programasHeredadosSincronizados$.next(affected);
        } catch { /* no-op */ }
      }

      return affected;
    } catch (e) {
      console.error('[Beneficiario-Local] Error en sincronizarProgramasDesdeContratante:', e);
      return 0;
    }
  }

  /** Une programas del contratante con los del beneficiario (switch ON) */
  private mergeProgramasHeredados(
    prevBeneficiario: ProgramaItemStore[],
    programasContratante: ProgramaItemStore[],
    programasExcluidos: string[] = []
  ): ProgramaItemStore[] {
    const prev = Array.isArray(prevBeneficiario) ? prevBeneficiario : [];
    const result: ProgramaItemStore[] = [];
    const usados = new Set<number>();
    const exclusiones = new Set(this.normalizarProgramasExcluidos(programasExcluidos));

    for (const ctr of programasContratante || []) {
      const ctrCode = this.normProgramaCode(ctr);
      if (!ctrCode) continue;

      // Override que reemplaza este programa del contratante
      const overrideIdx = prev.findIndex((p, idx) => {
        if (usados.has(idx)) return false;
        if ((p as any)?.personalizado !== true) return false;
        const origen = this.normProgramaCodeValue((p as any)?.codigoOrigenContratante);
        return !!origen && origen === ctrCode;
      });

      if (overrideIdx >= 0) {
        usados.add(overrideIdx);
        const override = prev[overrideIdx];
        result.push({
          ...override,
          personalizado: true,
          codigoOrigenContratante:
            ((override as any)?.codigoOrigenContratante || ctrCode).toString().trim() || ctrCode
        });
        continue;
      }

      // Lo quitó el usuario: no se vuelve a meter
      if (this.programaEstaExcluido(ctr, exclusiones)) {
        continue;
      }

      // Ya lo tenía: actualizar desde contratante y conservar ids
      const clone = this.limpiarIdsPersistenciaProgramas([ctr])[0];
      const sameCodeIdx = prev.findIndex((p, idx) =>
        !usados.has(idx) && this.normProgramaCode(p) === ctrCode
      );
      if (sameCodeIdx >= 0) {
        usados.add(sameCodeIdx);
        const prevP = prev[sameCodeIdx] as any;
        const mergedIds: any = {
          ...clone,
          personalizado: false,
          codigoOrigenContratante: null,
          idBenPrograma: Number(prevP.idBenPrograma || prevP.__idBenPrograma || 0) || clone.idBenPrograma,
          idSubcuenta: Number(prevP.idSubcuenta || prevP.__idSubcuenta || 0) || (clone as any).idSubcuenta,
          idCotizacionTarifa: Number(prevP.idCotizacionTarifa || prevP.__idCotizacionTarifa || 0) || clone.idCotizacionTarifa,
          idCotizacionTarifaAnexo: Number(prevP.idCotizacionTarifaAnexo || prevP.__idCotizacionTarifaAnexo || 0) || clone.idCotizacionTarifaAnexo,
          idCotizacionCampania: Number(
            prevP.idCotizacionCampania || prevP.__idCotizacionCampania || prevP.idBenCampania || 0
          ) || (clone as any).idCotizacionCampania,
          idBenCampania: Number(prevP.idBenCampania || prevP.idCotizacionCampania || 0) || clone.idBenCampania,
          __idBenPrograma: Number(prevP.__idBenPrograma || prevP.idBenPrograma || 0) || (clone as any).__idBenPrograma,
          __idSubcuenta: Number(prevP.__idSubcuenta || prevP.idSubcuenta || 0) || (clone as any).__idSubcuenta,
          __idCotizacionTarifa: Number(prevP.__idCotizacionTarifa || prevP.idCotizacionTarifa || 0) || (clone as any).__idCotizacionTarifa,
          __idCotizacionTarifaAnexo: Number(prevP.__idCotizacionTarifaAnexo || prevP.idCotizacionTarifaAnexo || 0) || (clone as any).__idCotizacionTarifaAnexo,
          __idCotizacionCampania: Number(
            prevP.__idCotizacionCampania || prevP.idCotizacionCampania || prevP.idBenCampania || 0
          ) || (clone as any).__idCotizacionCampania,
          guardadoEnBd: prevP.guardadoEnBd === true || clone.guardadoEnBd === true
        };
        result.push(mergedIds as ProgramaItemStore);
        continue;
      }

      // Programa nuevo del contratante
      result.push({
        ...clone,
        personalizado: false,
        codigoOrigenContratante: null
      });
    }

    // Overrides / extras que no entraron arriba
    prev.forEach((p, idx) => {
      if (usados.has(idx)) return;
      if ((p as any)?.personalizado !== true) return;

      const code = this.normProgramaCode(p);
      if (code && result.some(r => this.normProgramaCode(r) === code)) return;

      result.push({
        ...p,
        personalizado: true,
        codigoOrigenContratante:
          this.normProgramaCodeValue((p as any)?.codigoOrigenContratante) || null
      });
    });

    return result;
  }

  private calcularProgramasExcluidos(programasSeleccionados: ProgramaItemStore[]): string[] {
    const contratante = this.normalizarProgramasFuente(null);
    const kept = new Set<string>();
    (programasSeleccionados || []).forEach((p) => {
      const code = this.normProgramaCode(p);
      const name = this.normProgramaCodeValue((p as any)?.nombrePrograma || (p as any)?.nombre);
      const origen = this.normProgramaCodeValue((p as any)?.codigoOrigenContratante);
      if (code) kept.add(code);
      if (name) kept.add(name);
      if (origen) kept.add(origen);
    });
    return this.normalizarProgramasExcluidos(
      (contratante || [])
        .map((c) => this.normProgramaCode(c))
        .filter((code) => !!code && !kept.has(code))
    );
  }

  private unirProgramasExcluidos(
    previos: string[] | undefined,
    removidos: ProgramaItemStore[],
    criterio?: { programaCodigo?: string; programaNombre?: string; lineaNegocio?: string }
  ): string[] {
    const extra: string[] = [];
    (removidos || []).forEach((p) => {
      const code = this.normProgramaCode(p);
      const name = this.normProgramaCodeValue((p as any)?.nombrePrograma || (p as any)?.nombre);
      if (code) extra.push(code);
      if (name) extra.push(name);
    });
    if (criterio?.programaCodigo) extra.push(this.normProgramaCodeValue(criterio.programaCodigo));
    if (criterio?.programaNombre) extra.push(this.normProgramaCodeValue(criterio.programaNombre));
    return this.normalizarProgramasExcluidos([
      ...(Array.isArray(previos) ? previos : []),
      ...extra
    ]);
  }

  private normalizarProgramasExcluidos(raw: string[] | undefined | null): string[] {
    const uniq = new Set<string>();
    (Array.isArray(raw) ? raw : []).forEach((v) => {
      const n = this.normProgramaCodeValue(v);
      if (n) uniq.add(n);
    });
    return Array.from(uniq);
  }

  private programaEstaExcluido(programa: ProgramaItemStore, exclusiones: Set<string>): boolean {
    if (!exclusiones.size) return false;
    const code = this.normProgramaCode(programa);
    const name = this.normProgramaCodeValue((programa as any)?.nombrePrograma || (programa as any)?.nombre);
    return (!!code && exclusiones.has(code)) || (!!name && exclusiones.has(name));
  }

  /** Marca sync / override / extra al guardar con switch ON */
  aplicarMarcasPersonalizacion(
    programasNuevos: ProgramaItemStore[],
    prevProgramas?: ProgramaItemStore[] | null,
    programasContratanteFuente?: ProgramaItemStore[] | null
  ): ProgramaItemStore[] {
    const contratante = this.normalizarProgramasFuente(programasContratanteFuente);
    const prev = Array.isArray(prevProgramas) ? prevProgramas : [];
    const ctrCodes = new Set(
      (contratante || []).map(c => this.normProgramaCode(c)).filter(Boolean)
    );
    const nuevosCodes = new Set(
      (programasNuevos || []).map(p => this.normProgramaCode(p)).filter(Boolean)
    );
    const origenesAsignados = new Set<string>();

    return (programasNuevos || []).map((p) => {
      const code = this.normProgramaCode(p);
      if (!code) return { ...p };

      if (ctrCodes.has(code)) {
        return {
          ...p,
          personalizado: false,
          codigoOrigenContratante: null
        };
      }

      // Si ya era override, conservar el origen si sigue faltando
      const prevHit = prev.find(pr => {
        const prevCode = this.normProgramaCode(pr);
        return (pr as any)?.personalizado === true && prevCode === code;
      });
      const origenPrev = this.normProgramaCodeValue((prevHit as any)?.codigoOrigenContratante);
      if (origenPrev && !nuevosCodes.has(origenPrev) && !origenesAsignados.has(origenPrev)) {
        origenesAsignados.add(origenPrev);
        return {
          ...p,
          personalizado: true,
          codigoOrigenContratante: origenPrev
        };
      }

      // Reemplazo de un programa del contratante de la misma línea
      const ctrFaltanteMismaLinea = (contratante || []).find(c => {
        const cCode = this.normProgramaCode(c);
        if (!cCode || nuevosCodes.has(cCode)) return false;
        if (origenesAsignados.has(cCode)) return false;
        return this.mismaLineaNegocio(c, p);
      });

      if (ctrFaltanteMismaLinea) {
        const origen = this.normProgramaCode(ctrFaltanteMismaLinea);
        if (origen) origenesAsignados.add(origen);
        return {
          ...p,
          personalizado: true,
          codigoOrigenContratante: origen || null
        };
      }
      return {
        ...p,
        personalizado: true,
        codigoOrigenContratante: null
      };
    });
  }

  private normProgramaCode(p: ProgramaItemStore | null | undefined): string {
    return this.normProgramaCodeValue((p as any)?.programa || (p as any)?.id || '');
  }

  private normProgramaCodeValue(value: any): string {
    return (value ?? '').toString().trim().toUpperCase().split('|')[0].trim();
  }

  private mismaLineaNegocio(a: ProgramaItemStore, b: ProgramaItemStore): boolean {
    const alias = (v: any) => {
      const n = (v ?? '').toString().trim().toUpperCase();
      if (n === 'SO' || n === 'SAO' || n.includes('SALUD ORAL')) return 'SAO';
      if (n === 'MI' || n.includes('MEDICINA')) return 'MI';
      if (n === 'CEM' || n.includes('EMERGENCIA')) return 'CEM';
      return n;
    };
    const la = alias((a as any)?.lineaNegocio);
    const lb = alias((b as any)?.lineaNegocio);
    return !!la && !!lb && la === lb;
  }

  // helpers

  private normalizarProgramasFuente(programasFuente?: ProgramaItemStore[] | null): ProgramaItemStore[] {
    if (Array.isArray(programasFuente)) {
      return this.clonarProgramas(programasFuente);
    }
    return this.leerProgramasContratanteDesdeStorage();
  }

  private leerProgramasContratanteDesdeStorage(): ProgramaItemStore[] {
    try {
      const raw = localStorage.getItem(KEY_PROGRAMAS_CONTRATANTE);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      const items: any[] = Array.isArray(parsed)
        ? parsed
        : (parsed && typeof parsed === 'object' && Array.isArray(parsed.programas) ? parsed.programas : []);
      return this.clonarProgramas(items as ProgramaItemStore[]);
    } catch {
      return [];
    }
  }

  /** Arma edad/sexo/parentesco para recalcular tarifas tipo I */
  private construirDatosRecalculo(
    datos: DatosBeneficiarioLocal
  ): ProgramasBeneficiarioEnvelope['datosRecalculo'] {
    const edad = (datos?.edad ?? '0').toString().trim() || '0';
    const genero = (datos?.genero ?? '').toString().trim().toUpperCase();
    // 1 = F, 2 = M
    const sexo =
      genero === 'F' || genero === '1' || genero.slice(0, 1) === 'F'
        ? 'F'
        : 'M';
    const parentesco = (datos?.parentesco ?? '').toString().trim().toUpperCase();

    let cuenta: string | undefined;
    let tipoPlan: string | undefined;
    try {
      const raw = localStorage.getItem(KEY_DATOS_CONTRATANTE);
      if (raw) {
        const contratante = JSON.parse(raw);
        const cuentaRaw = (contratante?.cuenta ?? '').toString().trim();
        const tipoPlanRaw = (contratante?.tipoPlan ?? '').toString().trim();
        if (cuentaRaw) cuenta = cuentaRaw;
        if (tipoPlanRaw) tipoPlan = tipoPlanRaw;
      }
    } catch { /* no-op */ }

    return {
      edad,
      sexo,
      parentesco,
      ...(cuenta ? { cuenta } : {}),
      ...(tipoPlan ? { tipoPlan } : {})
    };
  }

  private leerDocumentoContratante(): { tipo: string; numero: string } | null {
    try {
      const raw = localStorage.getItem(KEY_DATOS_CONTRATANTE);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const tipo = (parsed?.tipoDocumento || '').toString().trim().toUpperCase();
      const numero = (parsed?.numeroDocumento || '').toString().trim();
      if (!tipo || !numero) return null;
      return { tipo, numero };
    } catch {
      return null;
    }
  }

  private esBeneficiarioTitularContratante(
    datos: DatosBeneficiarioLocal,
    docTitular: { tipo: string; numero: string } | null
  ): boolean {
    const parentesco = (datos?.parentesco || '').toString().trim().toUpperCase();
    if (parentesco === 'T' || parentesco === 'TITULAR') {
      return true;
    }
    if (!docTitular) return false;
    const tipo = (datos?.contratanteTipoDocumento || '').toString().trim().toUpperCase();
    const numero = (datos?.contratanteDocumento || '').toString().trim();
    return !!tipo && !!numero && tipo === docTitular.tipo && numero === docTitular.numero;
  }

  private clonarProgramas(programas: ProgramaItemStore[]): ProgramaItemStore[] {
    try {
      const cloned = JSON.parse(JSON.stringify(Array.isArray(programas) ? programas : [])) as ProgramaItemStore[];
      return this.limpiarIdsPersistenciaProgramas(cloned);
    } catch {
      return this.limpiarIdsPersistenciaProgramas(Array.isArray(programas) ? [...programas] : []);
    }
  }

  /** Quita ids de BD al heredar del contratante */
  limpiarIdsPersistenciaProgramas(programas: ProgramaItemStore[]): ProgramaItemStore[] {
    return (programas || []).map((p) => {
      const copy: any = { ...(p as any) };
      delete copy.idBeneficiario;
      delete copy.idBenPrograma;
      delete copy.idSubcuenta;
      delete copy.idCotizacionTarifa;
      delete copy.idCotizacionTarifaAnexo;
      delete copy.idCotizacionCampania;
      delete copy.idBenCampania;
      delete copy.__idBenPrograma;
      delete copy.__idSubcuenta;
      delete copy.__idCotizacionTarifa;
      delete copy.__idCotizacionTarifaAnexo;
      delete copy.__idCotizacionCampania;
      copy.guardadoEnBd = false;
      return copy as ProgramaItemStore;
    });
  }

  private clavesProgramaPersistencia(p: ProgramaItemStore | any): string[] {
    const raw = [
      (p as any)?.programa,
      (p as any)?.id,
      (p as any)?.nombrePrograma,
      (p as any)?.nombre,
      ((p as any)?.id || '').toString().split('|')[0],
      ((p as any)?.id || '').toString().split('-').pop()
    ];
    const uniq = new Set<string>();
    raw.forEach((v) => {
      const n = this.normProgramaCodeValue(v);
      if (n) uniq.add(n);
    });
    return Array.from(uniq);
  }

  /** Al editar, reusa ids de BD del programa anterior si el payload no los trae */
  private conservarIdsPersistenciaProgramas(
    prevProgramas: ProgramaItemStore[],
    nuevosProgramas: ProgramaItemStore[]
  ): ProgramaItemStore[] {
    const prev = Array.isArray(prevProgramas) ? prevProgramas : [];
    return (nuevosProgramas || []).map((nuevo) => {
      const keysNuevo = new Set(this.clavesProgramaPersistencia(nuevo));
      const prevHit = prev.find((p) => this.clavesProgramaPersistencia(p).some((k) => keysNuevo.has(k))) as any;
      if (!prevHit) return nuevo;
      const pickId = (...vals: any[]) => {
        for (const v of vals) {
          const n = Number(v || 0);
          if (n > 0) return n;
        }
        return null;
      };
      const merged: any = {
        ...nuevo,
        idBenPrograma: pickId((nuevo as any).idBenPrograma, (nuevo as any).__idBenPrograma, prevHit.idBenPrograma, prevHit.__idBenPrograma),
        idSubcuenta: pickId((nuevo as any).idSubcuenta, (nuevo as any).__idSubcuenta, prevHit.idSubcuenta, prevHit.__idSubcuenta),
        idCotizacionTarifa: pickId((nuevo as any).idCotizacionTarifa, (nuevo as any).__idCotizacionTarifa, prevHit.idCotizacionTarifa, prevHit.__idCotizacionTarifa),
        idCotizacionTarifaAnexo: pickId((nuevo as any).idCotizacionTarifaAnexo, (nuevo as any).__idCotizacionTarifaAnexo, prevHit.idCotizacionTarifaAnexo, prevHit.__idCotizacionTarifaAnexo),
        idCotizacionCampania: pickId(
          (nuevo as any).idCotizacionCampania,
          (nuevo as any).__idCotizacionCampania,
          (nuevo as any).idBenCampania,
          prevHit.idCotizacionCampania,
          prevHit.__idCotizacionCampania,
          prevHit.idBenCampania
        ),
        idBenCampania: pickId((nuevo as any).idBenCampania, prevHit.idBenCampania, prevHit.idCotizacionCampania),
        guardadoEnBd: (nuevo as any).guardadoEnBd === true || prevHit.guardadoEnBd === true
      };
      if (merged.idBenPrograma) merged.__idBenPrograma = merged.idBenPrograma;
      if (merged.idSubcuenta) merged.__idSubcuenta = merged.idSubcuenta;
      if (merged.idCotizacionTarifa) merged.__idCotizacionTarifa = merged.idCotizacionTarifa;
      if (merged.idCotizacionTarifaAnexo) merged.__idCotizacionTarifaAnexo = merged.idCotizacionTarifaAnexo;
      if (merged.idCotizacionCampania) merged.__idCotizacionCampania = merged.idCotizacionCampania;
      return merged as ProgramaItemStore;
    });
  }

  private programaCoincide(
    programa: ProgramaItemStore,
    criterio: { programaCodigo?: string; programaNombre?: string; lineaNegocio?: string }
  ): boolean {
    const norm = (v: any) => (v ?? '').toString().trim().toUpperCase();
    const aliasLinea = (v: any) => {
      const n = norm(v);
      if (n === 'SO' || n === 'SAO' || n.includes('SALUD ORAL')) return 'SAO';
      if (n === 'MI' || n.includes('MEDICINA')) return 'MI';
      if (n === 'CEM' || n.includes('EMERGENCIA')) return 'CEM';
      return n;
    };

    const pCode = norm((programa as any)?.programa || (programa as any)?.id || '');
    const pName = norm((programa as any)?.nombrePrograma || (programa as any)?.nombre || '');
    const pLinea = aliasLinea((programa as any)?.lineaNegocio || (programa as any)?.lineaNegocioNombre || '');

    const cCode = norm(criterio.programaCodigo);
    const cName = norm(criterio.programaNombre);
    const cLinea = aliasLinea(criterio.lineaNegocio);

    const matchProgram = (!!cCode && (pCode === cCode || pName === cCode))
      || (!!cName && (pName === cName || pCode === cName));
    const matchLinea = !cLinea || !pLinea || pLinea === cLinea;
    return matchProgram && matchLinea;
  }

  private lineasNegocioDesdeProgramas(programas: ProgramaItemStore[]): string {
    const aliasNombre = (linea: string): string => {
      const n = (linea || '').toString().trim().toUpperCase();
      if (n === 'MI') return 'MEDICINA INTEGRAL';
      if (n === 'SO' || n === 'SAO') return 'SALUD ORAL';
      if (n === 'CEM') return 'COOMEVA EMERGENCIAS MEDICAS';
      return linea;
    };
    const lineas = Array.from(new Set(
      (programas || [])
        .map(p => aliasNombre(((p as any)?.lineaNegocio || '').toString().trim()))
        .filter(Boolean)
    ));
    return lineas.join(', ');
  }

  private findIndexDatos(
    lista: DatosBeneficiarioLocal[],
    match: BeneficiarioLocalMatchKey
  ): number {
    const localId = (match.localId || '').toString().trim();
    if (localId) {
      const byId = lista.findIndex(item => (item?.localId || '').toString().trim() === localId);
      if (byId >= 0) return byId;
    }

    const tipo = (match.tipoDocumento || '').toString().trim().toUpperCase();
    const num = (match.numeroDocumento || '').toString().trim();
    const parentesco = (match.parentesco || '').toString().trim().toUpperCase();
    if (!tipo || !num) return -1;

    return lista.findIndex(item =>
      (item?.contratanteTipoDocumento || '').toString().trim().toUpperCase() === tipo &&
      (item?.contratanteDocumento || '').toString().trim() === num &&
      (item?.parentesco || '').toString().trim().toUpperCase() === parentesco
    );
  }

  private findIndexProgramas(
    lista: ProgramasBeneficiarioEnvelope[],
    match: BeneficiarioLocalMatchKey,
    fallbackIndex: number
  ): number {
    const localId = (match.localId || '').toString().trim();
    if (localId) {
      const byId = lista.findIndex(item => (item?.localId || '').toString().trim() === localId);
      if (byId >= 0) return byId;
    }

    const tipo = (match.tipoDocumento || '').toString().trim().toUpperCase();
    const num = (match.numeroDocumento || '').toString().trim();
    const parentesco = (match.parentesco || '').toString().trim().toUpperCase();
    if (tipo && num) {
      const byKey = lista.findIndex(item =>
        (item?.tipoDocumento || '').toString().trim().toUpperCase() === tipo &&
        (item?.numeroDocumento || '').toString().trim() === num &&
        (item?.parentesco || '').toString().trim().toUpperCase() === parentesco
      );
      if (byKey >= 0) return byKey;
    }

    return fallbackIndex >= 0 && fallbackIndex < lista.length ? fallbackIndex : -1;
  }

  private leerArreglo<T>(key: string): T[] {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }

  private escribir(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // no-op
    }
  }
}
